import React, { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Map from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useMapStore, useMapRef } from "../../store/useMapStore.js";
import { useProgressStore } from "../../store/progressStore.js";
import { GeometryType, Stage } from "../../constants/progress.js";
import { Equalareaprojection } from "../../utils/GeometryHelper.js";
import TileLayerSwitcher from "../MapControls/VectorTileLayerSwitch.jsx";
import GeocodingControl from "../MapControls/GeocodingControl";
import Confirm3DButton from "../Buttons/Confirm3DButton.jsx";
import ShareButton from "../Buttons/ShareButton.jsx";
import { getUTMZoneEPSG } from "../../services/GetUtmZone.js";
import { useCRSStore } from "../../store/useMapStore.js";
import { useBoundingBoxStore } from "../../store/useBoundingBoxStore.js";
import {useSiteExportStore } from "../../store/useSiteExport.js"

if (!maplibregl._patched) {
  const originalGetContext = HTMLCanvasElement.prototype.getContext;
  HTMLCanvasElement.prototype.getContext = function (type, attrs) {
    if (type === "webgl" || type === "webgl2") {
      return originalGetContext.call(this, type, {
        ...attrs,
        preserveDrawingBuffer: true,
      });
    }
    return originalGetContext.call(this, type, attrs);
  };
  maplibregl._patched = true;
}

const Map2DView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialLng  = parseFloat(searchParams.get("lng"))  || 13.4025;
  const initialLat  = parseFloat(searchParams.get("lat"))  || 52.5175;
  const initialZoom = parseFloat(searchParams.get("zoom")) || 16;

  const mapRef        = useRef();
  const captureMapRef = useRef(null);

  const [disableButton, setDisableButton]   = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isCapturing, setIsCapturing]       = useState(false);

  const [mapInstance, setMapInstance] = useState(null);

  const updateMapView     = useMapStore((state) => state.updateMapView);
  const updateMapref      = useMapRef((state) => state.updateMapref);
  const updateProgress    = useProgressStore((state) => state.updateProgress);
  const updateEPSGCRS     = useCRSStore((state) => state.updateEPSGCRS);
  const updateBoundingBox = useBoundingBoxStore((state) => state.updateBoundingBox);

  const updateTimeoutRef = useRef(null);
  const allEntitiesExport = useSiteExportStore((state) => state.entities);
  const clearAllentities = useSiteExportStore((state) => state.clearAllEntities);

  const debouncedUpdateMapView = useCallback(
    (center, zoom) => {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = setTimeout(
        () => updateMapView(center, zoom),
        200
      );
    },
    [updateMapView]
  );

  useEffect(() => () => clearTimeout(updateTimeoutRef.current), []);

  
  const handleMapLoad = useCallback(
    (evt) => {
      const map = evt.target; 
      setMapInstance(map);
      if (mapRef.current) updateMapref(mapRef);
    },
    [updateMapref]
  );

  const handleAddMarker = useCallback((coordinates) => {
    setMarkerPosition(coordinates);
  }, []);

  const handleConfirmClickRef = useRef(null);
  const isNavigatingRef       = useRef(false);

  handleConfirmClickRef.current = async () => {
  
    if (isNavigatingRef.current){
      console.warn("Click blocked — isNavigatingRef is true");
      return;
    } ;
    isNavigatingRef.current = true;

    try {
    
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
           
            const map       = mapRef.current;
            const mapBounds = map.getBounds();
            const ne        = mapBounds._ne;
            const sw        = mapBounds._sw;
            const mapcenter = map.getCenter();

            const epsgCode = getUTMZoneEPSG(mapcenter);
            updateEPSGCRS(epsgCode);
            updateBoundingBox(ne, sw);
            //BBOxRelativeToCenter([ne.lng, ne.lat, sw.lng, sw.lat], mapcenter);
           
            resolve();
          } catch (err) {
            console.log(err)
            reject(err);
          }
        }, 100);
      });

      const queryPayload = { stage: Stage.QUERY, progress: 25, error: null };

      updateProgress(GeometryType.SITE_PLOTS, {
        ...queryPayload,
        message: "Fetching site plots data from server",
      });
      updateProgress(GeometryType.BUILDINGS, {
        ...queryPayload,
        message: "Fetching buildings data from server",
      });
      updateProgress(GeometryType.TOPOGRAPHY, {
        ...queryPayload,
        message: "Fetching topography data from server",
      });

      sessionStorage.setItem("ShareURL", window.location.href);
      // navigate("/3d-view",{replace:true});
      navigate("/3d-view");
      
    } catch (error) {
      console.error("Error when clicking the weiter button", error);
      isNavigatingRef.current = false;
    }
  };

  const handleConfirmClick = useCallback(() => {
   
    clearAllentities();
    
    handleConfirmClickRef.current?.();
   
  }, [clearAllentities]);

  const handleMoveEnd = useCallback(
    (evt) => {
      if (isNavigatingRef.current) return;
      setSearchParams(
        (prev) => {
          prev.set("lng",  evt.viewState.longitude);
          prev.set("lat",  evt.viewState.latitude);
          prev.set("zoom", evt.viewState.zoom);
          return prev;
        },
        { replace: true }
      );

      const bounds = evt.target.getBounds();
      const area   = Equalareaprojection(bounds);

      if (area <= 1) {
        setDisableButton(false);
        debouncedUpdateMapView(
          {
            lat: evt.viewState.latitude,
            lng: evt.viewState.longitude,
            elv: 0,
          },
          evt.viewState.zoom
        );
      } else {
        setDisableButton(true);
      }
    },
    [setSearchParams, debouncedUpdateMapView]
  );

  return (
    <>
      <Map
        initialViewState={{
          longitude: initialLng,
          latitude:  initialLat,
          zoom:      initialZoom,
        }}
        maxZoom={18}
        onLoad={handleMapLoad}
        onMoveEnd={handleMoveEnd}
        style={{ width: "100%", height: "100%" }}
        ref={mapRef}
        mapLib={maplibregl}
        preserveDrawingBuffer={true}
      >
        
        {mapInstance && (
          <TileLayerSwitcher map={mapInstance} markerPosition={markerPosition} />
        )}
      </Map>

      <GeocodingControl mapRef={mapRef} show={true} onAddMarker={handleAddMarker} />
      <Confirm3DButton onClick={handleConfirmClick} disabled={disableButton} />
      <ShareButton />

      <div
        style={{
          position: "absolute",
          bottom: "3.75rem",
          left: "0.2rem",
          fontSize: "0.625rem",
          background: "rgba(255,255,255,0.8)",
          padding: "0.125rem 0.375rem",
          borderRadius: "0.25rem",
          color: "#333",
          zIndex: 9999,
          whiteSpace: "nowrap",
        }}
      >
        © TomTom | © Microsoft Azure Maps
      </div>
    </>
  );
};

export default Map2DView;